import { Stack, Typography } from '@mui/material';
import { z } from 'zod';

import { useAppForm } from '@/hooks/useAppForm';
import { useNavigationBlocker } from '@/hooks/useNavigationBlocker';
import { I18N_DE } from '@/i18n/de';
import type { Organization, UpdateUserDto, User } from '@/modules/api/backend';
import { UserRole } from '@/modules/api/backend';
import { zOrganizationUserRole } from '@/modules/api/backend/zod.gen';
import { formScrollIntoViewOptions } from '@/utils/formScrollIntoViewOptions';

interface EditUserFormFormProps {
  initialValues: User;
  onSubmit: (values: UpdateUserDto) => Promise<void>;
  organizations: Organization[];
}

export function EditUserForm({ initialValues, onSubmit, organizations }: EditUserFormFormProps) {
  const form = useAppForm({
    defaultValues: {
      status: initialValues.status,
      role: initialValues.role,
      organizationId: initialValues.organization?.organizationId,
      organizationUserRole: initialValues.organization?.role,
    },
    ...formScrollIntoViewOptions,
    onSubmit: async ({ value }) => {
      await onSubmit({
        status: value.status,
        role: value.role,
        organizationId: value.organizationId ?? undefined,
        organizationUserRole: value.organizationUserRole ?? undefined,
      });
    },
  });

  useNavigationBlocker(form);

  return (
    <form
      id="userForm"
      onSubmit={(event) => {
        event.preventDefault();
        event.stopPropagation();
        form.handleSubmit();
      }}
    >
      <Stack spacing={2}>
        <Typography variant="h2">User*in</Typography>

        <form.AppField name="role">
          {(field) => (
            <field.SelectField
              label="Globale Rolle"
              options={Object.entries(I18N_DE.UserRole).map(([value, label]) => ({
                value,
                label,
              }))}
            />
          )}
        </form.AppField>

        <form.AppField name="status">
          {(field) => (
            <field.SelectField
              label="Status"
              options={Object.entries(I18N_DE.UserStatus).map(([value, label]) => ({
                value,
                label,
              }))}
            />
          )}
        </form.AppField>

        <form.AppField
          name="organizationId"
          validators={{
            onChangeListenTo: ['role'],
            onChange: ({ value, fieldApi }) => {
              if (fieldApi.form.getFieldValue('role') !== UserRole.USER) return undefined;

              return z.string().nonempty().safeParse(value).error?.issues[0]?.message;
            },
          }}
        >
          {(field) => (
            <form.Subscribe selector={(s) => s.values.role === UserRole.USER}>
              {(isUser) => (
                <field.SelectField
                  disabled={!isUser}
                  label="Organisation"
                  sx={{ mb: 1 }}
                  options={organizations.map((org) => ({
                    value: org.id,
                    label: org.name,
                  }))}
                />
              )}
            </form.Subscribe>
          )}
        </form.AppField>

        <form.AppField
          name="organizationUserRole"
          validators={{
            onChangeListenTo: ['role'],
            onChange: ({ value, fieldApi }) => {
              if (fieldApi.form.getFieldValue('role') !== UserRole.USER) return undefined;

              return zOrganizationUserRole.safeParse(value).error?.issues[0]?.message;
            },
          }}
        >
          {(field) => (
            <form.Subscribe selector={(s) => s.values.role === UserRole.USER && !!s.values.organizationId}>
              {(enabled) => (
                <field.SelectField
                  disabled={!enabled}
                  label="Organisationsrolle"
                  options={Object.entries(I18N_DE.OrganizationUserRole).map(([value, label]) => ({
                    value,
                    label,
                  }))}
                />
              )}
            </form.Subscribe>
          )}
        </form.AppField>
      </Stack>
    </form>
  );
}
